import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Me } from "../api/users";
import {
  getAllFood,
  CreateRepastFoods,
  CreateRepastCombo,
  GetOpenCombo,
  GetUserRepastFoodByDate,
  getAllFieldsCombo,
  getAllFoodFieldsCombo,
  GetAllSelectedFoodByRepastId,
} from "../api/food";
import { userShift } from "../api/shift";
import { UserShiftMNG } from "../api/mng_shift";
import { GetRestaurants } from "../api/restaurant";
import { OpentypeShiftGet } from "../api/type_shift";

const WEEKDAYS = [
  "воскресенье",
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
];

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function formatDate(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDay(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function russianWeekday(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return WEEKDAYS[date.getDay()];
}

function sumHours(list) {
  return (Array.isArray(list) ? list : []).reduce(
    (sum, shift) => sum + (Number(shift.hours) || 0),
    0,
  );
}

const RecordCombo = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [chkShifts, setChkShifts] = useState([]);
  const [mngShifts, setMngShifts] = useState([]);
  const [hours, setHours] = useState(0);
  const [allCombo, setAllCombo] = useState([]);
  const [foodNames, setFoodNames] = useState({});
  const [recordedFoods, setRecordedFoods] = useState(null);
  const [selectedCombo, setSelectedCombo] = useState("");
  const [allFieldsCombo, setAllFieldsCombo] = useState([]);
  const [foodCombo, setFoodCombo] = useState({});
  const [selectedFoods, setSelectedFoods] = useState({});
  const [comboLoading, setComboLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const today = formatDate(new Date());
        const user = await Me(token);
        const [shifts, mng, restaurants, typeShifts] = await Promise.all([
          userShift(token, user.id, today, today),
          UserShiftMNG(token, user.id, today, today),
          GetRestaurants(token, 1, 1),
          OpentypeShiftGet(token),
        ]);

        const restNames = {};
        for (const rest of restaurants?.data || []) {
          restNames[rest.id] = rest.name;
        }
        const typeShiftNames = {};
        for (const typeShift of typeShifts || []) {
          typeShiftNames[typeShift.id] = typeShift.name;
        }

        const chk = Array.isArray(shifts) ? shifts : [];
        const mngList = Array.isArray(mng) ? mng : [];
        if (!cancelled) {
          setChkShifts(
            chk.map((shift) => ({
              id: shift.id,
              date: shift.date,
              start: shift.start,
              end: shift.end,
              station: shift.station_name,
              restaurant: shift.restaurant_name,
            })),
          );
          setMngShifts(
            mngList.map((shift) => ({
              id: shift.id,
              date: shift.date,
              start: shift.start,
              end: shift.end,
              station:
                Number(shift.type_shift_id) !== 1
                  ? typeShiftNames[shift.type_shift_id]
                  : shift.name,
              restaurant: restNames[shift.restaurant_id],
            })),
          );
        }

        const existing = await GetUserRepastFoodByDate(token, {
          date: today,
          user_id: user.id,
        });
        if (existing?.id) {
          const foods = await GetAllSelectedFoodByRepastId(token, existing.id);
          if (!cancelled) {
            setRecordedFoods(Array.isArray(foods) ? foods : []);
            setHours(sumHours(chk) || sumHours(mngList));
          }
          return;
        }

        if (chk.length === 0 && mngList.length === 0) {
          if (!cancelled) setHours(0);
          return;
        }

        const catalog = await getAllFood(token);
        const names = {};
        for (const food of catalog || []) names[food.id] = food.name;
        const shiftHours = chk.length ? sumHours(chk) : sumHours(mngList);
        const openFood = await GetOpenCombo(token);
        if (!cancelled) {
          setFoodNames(names);
          setHours(shiftHours);
          setAllCombo(Array.isArray(openFood) ? openFood : []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setMessage({ ok: false, text: "Не удалось загрузить приём пищи" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function selectCombo(comboId) {
    const id = String(comboId);
    setSelectedCombo(id);
    setMessage(null);
    setComboLoading(true);
    setAllFieldsCombo([]);
    setFoodCombo({});
    setSelectedFoods({});
    try {
      const fields = await getAllFieldsCombo(token, id);
      const list = Array.isArray(fields) ? fields : [];
      const nextFoods = {};
      const nextSelected = {};
      await Promise.all(
        list.map(async (field) => {
          nextSelected[field.id] = "";
          const foods = await getAllFoodFieldsCombo(token, field.id);
          nextFoods[field.id] = (Array.isArray(foods) ? foods : []).map(
            (item) => ({
              id: item.food_id,
              name: foodNames[item.food_id] || `Блюдо ${item.food_id}`,
            }),
          );
        }),
      );
      setFoodCombo(nextFoods);
      setAllFieldsCombo(list);
      setSelectedFoods(nextSelected);
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Не удалось загрузить состав комбо" });
    } finally {
      setComboLoading(false);
    }
  }

  function selectFood(fieldId, foodId) {
    setSelectedFoods((prev) => ({ ...prev, [fieldId]: String(foodId) }));
    setMessage(null);
  }

  const selectedCount = useMemo(
    () =>
      Object.values(selectedFoods).filter(
        (value) => value && value !== "0",
      ).length,
    [selectedFoods],
  );
  const isComplete =
    allFieldsCombo.length > 0 && selectedCount === allFieldsCombo.length;

  async function recordRepast(event) {
    event.preventDefault();
    if (!isComplete || submitting) return;

    for (const field of allFieldsCombo) {
      const value = selectedFoods[field.id];
      if (!value || value === "0") {
        setMessage({ ok: false, text: "Выберите еду во всех полях" });
        return;
      }
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const newRepast = await CreateRepastCombo(token, selectedCombo);
      if (!newRepast?.id) {
        setMessage({
          ok: false,
          text: errorText(newRepast?.data, "Не удалось записать приём пищи"),
        });
        return;
      }
      const payload = allFieldsCombo.map((field) => ({
        repast_id: newRepast.id,
        food_id: selectedFoods[field.id],
      }));
      const created = await CreateRepastFoods(token, payload);
      const items = (Array.isArray(created) ? created : []).map((item) => ({
        id: item.food_id,
        name: foodNames[item.food_id] || `Блюдо ${item.food_id}`,
      }));
      setRecordedFoods(items);
      setMessage({ ok: true, text: "Приём пищи записан" });
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Не удалось записать приём пищи" });
    } finally {
      setSubmitting(false);
    }
  }

  const sortedCombos = useMemo(
    () =>
      [...allCombo].sort(
        (a, b) => (Number(a.min_hours) || 0) - (Number(b.min_hours) || 0),
      ),
    [allCombo],
  );
  const selectedComboMeta = sortedCombos.find(
    (combo) => String(combo.id) === String(selectedCombo),
  );
  const hasShifts = chkShifts.length > 0 || mngShifts.length > 0;

  return (
    <div className="repast-page">
      <div className="reports-header">
        <div>
          <h1>Приём пищи</h1>
          <p className="reports-hint">
            Выберите комбо по часам смены и блюда в каждом поле
          </p>
        </div>
        <Link to="/profile" className="reports-back-link">
          ← К профилю
        </Link>
      </div>

      {loading ? (
        <div className="reports-loading">Загрузка смен и комбо…</div>
      ) : (
        <>
          <div className="chk-summary-kpis repast-kpis">
            <div className="chk-summary-kpi">
              <span>Часы сегодня</span>
              <strong>{hours}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>Смены</span>
              <strong>{chkShifts.length + mngShifts.length}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>Доступно комбо</span>
              <strong>{recordedFoods ? "—" : sortedCombos.length}</strong>
            </div>
          </div>

          <ShiftTable title="Смены ЧК" rows={chkShifts} />
          <ShiftTable title="Смены МНГ" rows={mngShifts} />

          {!hasShifts ? (
            <div className="users-empty">
              Сегодня нет смен — записать приём пищи нельзя
            </div>
          ) : recordedFoods ? (
            <section className="repast-done">
              <div className="chk-approve-summary is-ok">
                <div>
                  <h4>Сегодняшний приём пищи уже записан</h4>
                  <p>Повторно выбрать комбо сегодня нельзя</p>
                </div>
              </div>
              <ul className="repast-done-list">
                {recordedFoods.map((food) => (
                  <li key={food.id || food.name}>{food.name}</li>
                ))}
              </ul>
            </section>
          ) : (
            <form className="repast-form" onSubmit={recordRepast}>
              <section className="users-list repast-card">
                <div className="users-list-head">
                  <h2>Комбо</h2>
                  <span className="users-count">от {hours} ч смены</span>
                </div>
                {sortedCombos.length === 0 ? (
                  <div className="users-empty">
                    Нет доступных комбо для ваших часов. Нужно больше часов
                    смены.
                  </div>
                ) : (
                  <div className="repast-combos">
                    {sortedCombos.map((combo) => {
                      const active = String(combo.id) === String(selectedCombo);
                      return (
                        <button
                          type="button"
                          key={combo.id}
                          className={`repast-combo${active ? " is-active" : ""}`}
                          onClick={() => selectCombo(combo.id)}
                        >
                          <strong>{combo.name}</strong>
                          <span>от {combo.min_hours} ч</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              {comboLoading ? (
                <div className="reports-loading">Загрузка блюд…</div>
              ) : null}

              {allFieldsCombo.length > 0 && !comboLoading ? (
                <section className="users-list repast-card">
                  <div className="users-list-head">
                    <h2>
                      Состав
                      {selectedComboMeta ? ` · ${selectedComboMeta.name}` : ""}
                    </h2>
                    <span className="users-count">
                      {selectedCount} / {allFieldsCombo.length}
                    </span>
                  </div>
                  {allFieldsCombo.map((field) => (
                    <div className="repast-field" key={field.id}>
                      <p className="repast-field-title">{field.name}</p>
                      <div className="repast-foods">
                        {(foodCombo[field.id] || []).map((food) => {
                          const active =
                            String(selectedFoods[field.id]) === String(food.id);
                          return (
                            <button
                              type="button"
                              key={food.id}
                              className={`repast-food${active ? " is-active" : ""}`}
                              onClick={() => selectFood(field.id, food.id)}
                            >
                              {food.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </section>
              ) : null}

              {message ? (
                <div className={message.ok ? "login-success" : "login-error"}>
                  {message.text}
                </div>
              ) : null}

              {allFieldsCombo.length > 0 && !comboLoading ? (
                <button
                  type="submit"
                  className="btn login-submit"
                  disabled={!isComplete || submitting}
                >
                  {submitting ? "Сохранение…" : "Записать приём пищи"}
                </button>
              ) : null}
            </form>
          )}
        </>
      )}
    </div>
  );
};

function ShiftTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <section className="users-list repast-shifts">
      <div className="users-list-head">
        <h2>{title}</h2>
      </div>
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>День</th>
              <th>Время</th>
              <th>Станция</th>
              <th>Ресторан</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((shift) => (
              <tr key={shift.id}>
                <td>{formatDay(shift.date)}</td>
                <td>{russianWeekday(shift.date)}</td>
                <td>
                  {shift.start} – {shift.end}
                </td>
                <td>{shift.station || "—"}</td>
                <td>{shift.restaurant || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default RecordCombo;
